import { Fragment, useId, useRef, useState, type ReactNode } from 'react';
import { ORIGINAL_IMAGE_ID } from '@vd/annotation-core';
import { Plus, Upload, Monitor, X, Check } from 'lucide-react';
import { Menu, MenuItem, MenuDivider, Popover, Tooltip, useToast, cn } from '@vd/ui';
import styles from './ImageCarousel.module.css';
import { formatClock } from '../../lib/time';

export interface StageImage {
  id: string;
  label: string;
  url?: string;
}

/**
 * Sentinel id for the capture/screenshot slot. Canonical definition
 * lives in @vd/annotation-core (normalizeShape pins legacy shapes to
 * it); re-exported here so carousel callers keep one import site.
 */
export { ORIGINAL_IMAGE_ID };

/**
 * Format a list of image labels for a success toast. Inline-lists short
 * batches verbatim; collapses longer ones to "first, second, and N more"
 * so the toast doesn't run on for a 20-file selection.
 */
function summarizeLabels(items: StageImage[]): string {
  if (items.length <= 3) return items.map((i) => i.label).join(', ');
  return `${items[0]!.label}, ${items[1]!.label}, and ${items.length - 2} more`;
}

export interface ImageCarouselProps {
  images: StageImage[];
  activeId: string;
  onSelect: (id: string) => void;
  /**
   * Called with the newly-added image(s). Receives an array so a single
   * multi-file selection lands as one parent state update rather than N
   * sequential setStates (each of which would re-render the carousel
   * and clear transient editor state via the parent's selectImage).
   */
  onAdd: (images: StageImage[]) => void;
  /**
   * Called when the user clicks the × on a thumbnail. Optional —
   * carousel hides the × affordance when not provided. The original
   * capture slot is never removable regardless (see below).
   */
  onRemove?: (id: string) => void;
  /**
   * Placed just after the first image, the capture: the walkthrough clip's
   * tile (t-cnb0zy). Before the add button when there is no image at all.
   */
  afterFirst?: ReactNode;
}

/**
 * Shows the original screenshot plus any uploaded images as selectable thumbnails.
 * The "+" button opens a menu with two options:
 *   - Upload image (file picker, multi-select)
 *   - Capture screen (via getDisplayMedia, single frame)
 *
 * Non-original thumbnails show a × on hover when `onRemove` is wired.
 */
export function ImageCarousel({ images, activeId, onSelect, onAdd, onRemove, afterFirst }: ImageCarouselProps) {
  const [menuAnchor, setMenuAnchor] = useState<DOMRect | null>(null);
  const [capturing, setCapturing] = useState(false);
  // Files held over the strip: the drop box says it will take them.
  const [dragging, setDragging] = useState(false);
  const plusBtn = useRef<HTMLButtonElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const dropHint = useId();
  const toast = useToast();

  const openUpload = () => {
    setMenuAnchor(null);
    fileInput.current?.click();
  };

  // Read N files in parallel, preserve picker order in the result, then
  // hand the whole batch to the parent in one onAdd call. Non-image
  // files are dropped with a single rolled-up warning so a typo'd
  // selection doesn't spam one toast per skipped file.
  const handleFiles = async (files: File[]) => {
    if (files.length === 0) return;
    // Only formats the server persists (TOADO-261: png/jpeg/webp/gif).
    // Accepting anything image/* here meant an SVG or HEIC got a
    // success toast, displayed locally, and silently never reached
    // teammates.
    const SERVER_FORMATS = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
    const accepted = files.filter((f) => SERVER_FORMATS.has(f.type));
    const skipped = files.length - accepted.length;
    if (skipped > 0) {
      toast.push({
        variant: skipped === files.length ? 'danger' : 'warning',
        title: skipped === files.length ? 'No images selected' : 'Some files skipped',
        description: `Skipped ${skipped} non-image file${skipped === 1 ? '' : 's'}.`,
      });
    }
    if (accepted.length === 0) return;

    const read = (file: File) => new Promise<StageImage | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve({
        id: 'img-' + Math.random().toString(36).slice(2, 14),
        label: file.name,
        url: reader.result as string,
      });
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });

    const results = await Promise.all(accepted.map(read));
    const added = results.filter((r): r is StageImage => r !== null);
    const failed = results.length - added.length;
    if (added.length === 0) {
      toast.push({ variant: 'danger', title: 'Upload failed', description: 'No images could be read.' });
      return;
    }
    onAdd(added);
    toast.push({
      variant: 'success',
      title: added.length === 1 ? 'Image added' : `${added.length} images added`,
      description: failed > 0 ? `(${failed} failed to read)` : summarizeLabels(added),
    });
  };

  const captureScreen = async () => {
    setMenuAnchor(null);
    if (!('mediaDevices' in navigator) || typeof navigator.mediaDevices.getDisplayMedia !== 'function') {
      toast.push({
        variant: 'warning',
        title: 'Screen capture not supported',
        description: 'Your browser doesn\'t expose getDisplayMedia. Try Chrome or Edge.',
      });
      return;
    }
    setCapturing(true);
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 1 }, audio: false });
      const track = stream.getVideoTracks()[0]!;
      const settings = track.getSettings();

      // Grab a single frame via a hidden <video> + canvas (works everywhere).
      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      await video.play();
      await new Promise((r) => setTimeout(r, 250)); // let the frame render

      const canvas = document.createElement('canvas');
      canvas.width = settings.width ?? video.videoWidth ?? 1920;
      canvas.height = settings.height ?? video.videoHeight ?? 1080;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas unsupported');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const dataUrl = canvas.toDataURL('image/png');
      stream.getTracks().forEach((t) => t.stop());

      onAdd([{
        id: 'cap-' + Math.random().toString(36).slice(2, 14),
        label: `Screen ${formatClock(new Date(), { ms: false })}`,
        url: dataUrl,
      }]);
      toast.push({ variant: 'success', title: 'Screen captured', description: 'Added to this ticket.' });
    } catch (err) {
      if ((err as Error).name !== 'NotAllowedError') {
        toast.push({ variant: 'danger', title: 'Capture failed', description: (err as Error).message });
      }
    } finally {
      setCapturing(false);
    }
  };

  // Only a drag that carries files is offered the drop: a dragged thumbnail
  // or a selection of text is not an upload.
  const carriesFiles = (e: React.DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');

  return (
    <div
      className={styles.carousel}
      data-testid="image-carousel"
      onDragEnter={(e) => { if (carriesFiles(e)) { e.preventDefault(); setDragging(true); } }}
      onDragOver={(e) => { if (carriesFiles(e)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } }}
      onDragLeave={(e) => {
        // Leaving for one of its own children is not leaving the strip.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={(e) => {
        if (!carriesFiles(e)) return;
        e.preventDefault();
        setDragging(false);
        void handleFiles(Array.from(e.dataTransfer.files));
      }}
    >
      {images.map((img, index) => {
        // The original capture stays — it represents the underlying
        // ticket asset, not a user-added image. Removing it from
        // the carousel wouldn't actually delete it server-side and
        // would re-appear on the next ticket refetch (the
        // screenshotUrl-patcher in AnnotationCanvas re-adds it).
        const canRemove = !!onRemove && img.id !== ORIGINAL_IMAGE_ID;
        return (
          <Fragment key={img.id}>
            <Tooltip content={img.label}>
              <span className={styles.thumbWrap}>
                <button
                  className={cn(styles.thumb, img.id === activeId && styles.thumbActive)}
                  onClick={() => onSelect(img.id)}
                  style={img.url ? { backgroundImage: `url(${img.url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
                  data-testid="carousel-thumb"
                  data-image-id={img.id}
                >
                  {img.id === activeId && <Check size={12} className={styles.activeMark} />}
                </button>
                {canRemove && (
                  <button
                    type="button"
                    className={styles.removeBtn}
                    // The × is a sibling of the thumb button, not a
                    // child, so the click won't naturally bubble TO the
                    // thumb. But stopping propagation defends against
                    // any future ancestor click handler (carousel,
                    // Tooltip wrapper) silently swallowing the remove
                    // intent. preventDefault avoids any browser-default
                    // button behavior; mousedown stop keeps focus from
                    // escaping into the thumb's selection logic.
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onRemove!(img.id);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    aria-label={`Remove ${img.label}`}
                    data-testid="carousel-remove"
                    data-image-id={img.id}
                  >
                    <X size={10} />
                  </button>
                )}
              </span>
            </Tooltip>
            {index === 0 && afterFirst}
          </Fragment>
        );
      })}
      {images.length === 0 && afterFirst}

      {/* The way to add one: a box that takes the strip's spare room, with
          a dashed edge and a plus, that a click opens and files dropped
          anywhere on the strip land in (Jason, 23 Oct). */}
      <button
        ref={plusBtn}
        type="button"
        className={cn(styles.dropBox, dragging && styles.dropBoxActive)}
        onClick={() => setMenuAnchor(plusBtn.current?.getBoundingClientRect() ?? null)}
        disabled={capturing}
        aria-label="Add image"
        aria-describedby={dropHint}
        data-testid="carousel-drop"
        data-dragging={dragging || undefined}
      >
        <Plus size={14} aria-hidden />
        <span id={dropHint}>{dragging ? 'Drop to add' : 'Drop images here, or click to add one'}</span>
      </button>

      <Popover open={!!menuAnchor} onClose={() => setMenuAnchor(null)} anchor={menuAnchor} placement="top-start">
        <Menu>
          <MenuItem leftIcon={<Upload size={14} />} onClick={openUpload}>
            Upload image…
          </MenuItem>
          <MenuItem leftIcon={<Monitor size={14} />} onClick={captureScreen}>
            Capture screen…
          </MenuItem>
          <MenuDivider />
          <div style={{ padding: '6px 10px 8px', fontSize: 11, color: 'var(--text-tertiary)' }}>
            Use the Chrome extension to capture a live web page with full DevTools context.
          </div>
        </Menu>
      </Popover>

      <input
        ref={fileInput}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        multiple
        hidden
        onChange={(e) => {
          const files = e.target.files ? Array.from(e.target.files) : [];
          // Reset before the async read so a repeat selection of the
          // same files still fires onChange (browsers de-dupe identical
          // .files arrays). Fire-and-forget the read; toasts handle UX.
          e.currentTarget.value = '';
          void handleFiles(files);
        }}
      />

      {capturing && (
        <div className={styles.capturing}>
          <span className={styles.pulse} /> Capturing…
          <button
            onClick={() => { setCapturing(false); }}
            aria-label="Cancel"
            style={{ marginLeft: 8, padding: 2, color: 'var(--text-tertiary)' }}
          >
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
}
